import React, { useEffect, useMemo, useState } from "react";
import { useCart } from "../../hooks/CartContext";
import { FaPlus, FaMinus, FaTrash } from "react-icons/fa";
import { FaRegHeart } from "react-icons/fa6";
import toast from "react-hot-toast";
import useAuthUser from "../../components/common/useAuthUser";
import Cookies from "js-cookie";
import { Link, useNavigate } from "react-router-dom";

const Cart = () => {
  const navigate = useNavigate();
  const token = Cookies.get("gameport");
  const { cart, removeFromCart, updateQuantity, clearCart } = useCart();
  const { authUser } = useAuthUser();

  const [shippingAddress, setShippingAddress] = useState({
    address: "",
    city: "",
    country: "Nigeria",
    telephone: "",
    email: "",
    name: "",
  });

  useEffect(() => {
    if (authUser) {
      setShippingAddress((prev) => ({
        ...prev,
        email: prev.email || authUser?.email || "",
        name: prev.name || authUser?.fullName || authUser?.name || "",
      }));
    }
  }, [authUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const totalQuantity = useMemo(() => {
    return cart.reduce((acc, item) => acc + item.cartQuantity, 0);
  }, [cart]);

  const totalPrice = useMemo(() => {
    return cart.reduce(
      (acc, item) =>
        acc + Number(item.finalPrice || 0) * Number(item.cartQuantity || 0),
      0
    );
  }, [cart]);

  const canContinue = useMemo(() => {
    const s = shippingAddress;
    return (
      !!token &&
      cart.length > 0 &&
      totalPrice > 0 &&
      s.email.trim() &&
      s.name.trim() &&
      s.address.trim() &&
      s.city.trim() &&
      s.country.trim() &&
      s.telephone.trim()
    );
  }, [token, cart.length, totalPrice, shippingAddress]);

  const checkoutPayload = useMemo(() => {
    return {
      items: cart.map((item) => ({
        product: item._id,
        name: item.name,
        finalPrice: Number(item.finalPrice || 0),
        quantity: Number(item.cartQuantity || 0),
      })),
      shippingAddress: {
        address: shippingAddress.address,
        city: shippingAddress.city,
        country: shippingAddress.country,
        telephone: shippingAddress.telephone,
        email: shippingAddress.email,
        name: shippingAddress.name,
      },
      totalPrice,
      totalQuantity,
    };
  }, [cart, shippingAddress, totalPrice, totalQuantity]);

  const handleProceedToPayment = () => {
    if (!token) {
      toast.error("Please sign in to continue.");
      navigate("/signin");
      return;
    }

    if (!canContinue) {
      toast.error("Please complete your shipping details.");
      return;
    }

    sessionStorage.setItem("checkout_payload", JSON.stringify(checkoutPayload));
    navigate("/checkout/payment");
  };

  const handleIncrease = (item) => {
    if (
      typeof item.quantity === "number" &&
      item.cartQuantity >= item.quantity
    ) {
      toast.error("You have reached the available stock.");
      return;
    }

    updateQuantity(item._id, item.cartQuantity + 1);
  };

  const handleDecrease = (item) => {
    updateQuantity(item._id, Math.max(1, item.cartQuantity - 1));
  };

  const inputBase =
    "w-full border-0 border-b-2 border-gray-300 bg-transparent px-0 py-2.5 text-base text-gray-900 placeholder:text-gray-400 focus:border-black focus:outline-none focus:ring-0";

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-10">
        <div className="mb-6 flex flex-col gap-3 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl">
            Your Cart
          </h1>

          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
            <button
              type="button"
              onClick={clearCart}
              disabled={cart.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red px-4 py-2.5 font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Clear Cart <FaTrash />
            </button>

            <Link
              to="/wishList"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red px-4 py-2.5 font-semibold text-white transition hover:opacity-90"
            >
              WishList <FaRegHeart />
            </Link>
          </div>
        </div>

        {cart.length === 0 ? (
          <div className="rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
            <p className="text-lg font-semibold text-gray-900">
              Your cart is empty
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Add products to your cart to continue shopping.
            </p>

            <Link
              to="/consoles"
              className="mt-5 inline-flex rounded-xl bg-red px-5 py-2.5 font-semibold text-white transition hover:opacity-90"
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
            {/* Cart Items */}
            <div className="space-y-4 lg:col-span-2">
              {cart.map((item) => (
                <div
                  key={item._id}
                  className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5"
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div
                      className="flex min-w-0 cursor-pointer items-center gap-4"
                      onClick={() => navigate(`/product/${item._id}`)}
                    >
                      <img
                        src={item.images?.[0] || "/placeholder.png"}
                        alt={item.name}
                        className="h-20 w-20 shrink-0 rounded-xl border border-gray-100 object-cover sm:h-24 sm:w-24"
                      />

                      <div className="min-w-0">
                        <h2 className="truncate font-semibold text-gray-900">
                          {item.name}
                        </h2>
                        <p className="text-sm text-gray-600">
                          ₦{Number(item.finalPrice).toLocaleString()}
                        </p>

                        {typeof item.quantity === "number" && (
                          <p className="text-sm text-red">
                            {item.quantity} unit{item.quantity !== 1 ? "s" : ""} left
                          </p>
                        )}

                        <p className="mt-1 text-sm font-semibold text-gray-900">
                          Subtotal: ₦
                          {Number(
                            item.finalPrice * item.cartQuantity
                          ).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 sm:justify-end">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleDecrease(item)}
                          className="rounded-xl bg-gray-100 p-2.5 transition hover:bg-gray-200"
                        >
                          <FaMinus />
                        </button>

                        <span className="w-10 text-center font-semibold text-gray-800">
                          {item.cartQuantity}
                        </span>

                        <button
                          type="button"
                          onClick={() => handleIncrease(item)}
                          className="rounded-xl bg-gray-100 p-2.5 transition hover:bg-gray-200"
                        >
                          <FaPlus />
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeFromCart(item._id)}
                        className="rounded-xl bg-red p-2.5 text-white transition hover:opacity-90"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="h-max rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6 lg:sticky lg:top-6">
              <h2 className="mb-4 text-xl font-semibold text-gray-900 sm:text-2xl">
                Summary
              </h2>

              <div className="flex justify-between text-sm text-gray-600">
                <span>Items ({totalQuantity})</span>
                <span>₦{totalPrice.toLocaleString()}</span>
              </div>

              <div className="my-4 border-t border-gray-200" />

              <div className="mb-6 flex justify-between text-lg font-bold text-gray-900">
                <span>Total</span>
                <span>₦{totalPrice.toLocaleString()}</span>
              </div>

              {!token ? (
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-sm text-gray-700">
                    Please sign in to complete your checkout and place your order.
                  </p>
                  <Link
                    to="/signin"
                    className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-red px-4 py-3 font-semibold text-white transition hover:opacity-90"
                  >
                    Sign In to Checkout
                  </Link>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                      Shipping Address
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Enter where your order should be delivered.
                    </p>
                  </div>

                  <form className="space-y-5">
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-700">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={shippingAddress.email}
                        onChange={handleChange}
                        className={inputBase}
                        placeholder="you@example.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-700">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={shippingAddress.name}
                        onChange={handleChange}
                        className={inputBase}
                        placeholder="Full name"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-700">
                        Address
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={shippingAddress.address}
                        onChange={handleChange}
                        className={inputBase}
                        placeholder="Street, area, landmark"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-700">
                          City
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={shippingAddress.city}
                          onChange={handleChange}
                          className={inputBase}
                          placeholder="City"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-700">
                          Country
                        </label>
                        <input
                          type="text"
                          name="country"
                          value={shippingAddress.country}
                          onChange={handleChange}
                          className={inputBase}
                          placeholder="Country"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-700">
                        Telephone
                      </label>
                      <input
                        type="tel"
                        name="telephone"
                        value={shippingAddress.telephone}
                        onChange={handleChange}
                        className={inputBase}
                        placeholder="Phone number"
                      />
                    </div>
                  </form>

                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={handleProceedToPayment}
                      disabled={!canContinue}
                      className={`w-full rounded-xl py-3 text-base font-semibold transition sm:text-lg ${
                        !canContinue
                          ? "cursor-not-allowed bg-gray-300 text-gray-700"
                          : "bg-red text-white hover:opacity-90"
                      }`}
                    >
                      Place Order
                    </button>

                    {!canContinue && (
                      <p className="mt-2 text-xs text-gray-500">
                        Fill all shipping details to continue.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;